import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { accessLocalStorage, getPortRedirects, createPortRedirect, deletePortRedirect } from '../../../services';
import ConfirmModal from '../../Common/ConfirmModal';
import {
  DESTINATION_PORTS,
  EMPTY_FORM,
  PORT_REDIRECT_MESSAGES,
  PROTOCOL_LABELS,
  buildPayload,
  errorMessageFor,
  filterRules,
  toRow,
} from './portRedirectScreen';

import '../../../libs/bulma.min.css';
import './PortRedirects.css';

function PortRedirects() {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [appliedTerm, setAppliedTerm] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const fetchRules = useCallback(async () => {
    const result = await getPortRedirects();
    if (result.success) {
      setRules(result.data);
      setLoadError(null);
    } else {
      setRules([]);
      setLoadError(errorMessageFor(result, PORT_REDIRECT_MESSAGES.loadFailed));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!accessLocalStorage.getUserLocalStorage()) {
      history.push('/');
      return;
    }
    fetchRules();
  }, [history, fetchRules]);

  const visibleRows = useMemo(() => filterRules(rules, appliedTerm).map(toRow), [rules, appliedTerm]);

  const applyFilter = () => setAppliedTerm(searchInput);

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      applyFilter();
    }
  };

  const openForm = () => {
    setForm(EMPTY_FORM);
    setFormError(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setFormError(null);
  };

  const updateField = (field) => (event) => setForm({ ...form, [field]: event.target.value });

  const submitForm = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    const result = await createPortRedirect(buildPayload(form));
    setSaving(false);
    if (!result.success) {
      setFormError(errorMessageFor(result, PORT_REDIRECT_MESSAGES.saveFailed));
      return;
    }
    setFormOpen(false);
    setForm(EMPTY_FORM);
    await fetchRules();
  };

  const openDelete = (row) => {
    setDeleteError(null);
    setRuleToDelete(row);
  };

  const closeDelete = () => {
    if (deleting) return;
    setRuleToDelete(null);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!ruleToDelete || deleting) return;
    setDeleting(true);
    const result = await deletePortRedirect(ruleToDelete.id);
    setDeleting(false);
    if (!result.success) {
      setDeleteError(errorMessageFor(result, PORT_REDIRECT_MESSAGES.deleteFailed));
      return;
    }
    setRuleToDelete(null);
    await fetchRules();
  };

  return (
    <div className="port-redirects-container">
      <div className="port-redirects-header">
        <h1 className="title is-4">Redirect de Portas</h1>
        <div className="port-redirects-toolbar">
          <input
            className="input"
            type="text"
            placeholder="Buscar por IP, porta ou protocolo"
            aria-label="Buscar"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
          <button type="button" className="button is-light" onClick={applyFilter}>Filtrar</button>
          <button type="button" className="button is-primary" onClick={openForm}>Adicionar Porta</button>
        </div>
      </div>

      {formOpen && (
        <form className="box port-redirects-form" onSubmit={submitForm}>
          <div className="columns is-multiline">
            <div className="column is-4">
              <label className="label" htmlFor="port-redirect-source-ip">IP de origem (opcional)</label>
              <input
                id="port-redirect-source-ip"
                className="input"
                type="text"
                placeholder="Qualquer"
                value={form.sourceIp}
                onChange={updateField('sourceIp')}
              />
            </div>
            <div className="column is-2">
              <label className="label" htmlFor="port-redirect-start">Porta inicial</label>
              <input
                id="port-redirect-start"
                className="input"
                type="text"
                inputMode="numeric"
                value={form.sourcePortStart}
                onChange={updateField('sourcePortStart')}
              />
            </div>
            <div className="column is-2">
              <label className="label" htmlFor="port-redirect-end">Porta final (opcional)</label>
              <input
                id="port-redirect-end"
                className="input"
                type="text"
                inputMode="numeric"
                value={form.sourcePortEnd}
                onChange={updateField('sourcePortEnd')}
              />
            </div>
            <div className="column is-2">
              <label className="label" htmlFor="port-redirect-protocol">Protocolo</label>
              <div className="select is-fullwidth">
                <select id="port-redirect-protocol" value={form.protocol} onChange={updateField('protocol')}>
                  {Object.entries(PROTOCOL_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="column is-2">
              <label className="label" htmlFor="port-redirect-destination">Porta de destino</label>
              <div className="select is-fullwidth">
                <select id="port-redirect-destination" value={form.destinationPort} onChange={updateField('destinationPort')}>
                  {DESTINATION_PORTS.map((port) => <option key={port} value={port}>{port}</option>)}
                </select>
              </div>
            </div>
          </div>
          <p className="help port-redirects-help">{PORT_REDIRECT_MESSAGES.help}</p>
          {formError && <div className="notification is-danger is-light port-redirects-form-error">{formError}</div>}
          <div className="buttons">
            <button type="submit" className={`button is-primary${saving ? ' is-loading' : ''}`} disabled={saving}>Salvar</button>
            <button type="button" className="button is-light" onClick={closeForm} disabled={saving}>Cancelar</button>
          </div>
        </form>
      )}

      {loadError && <div className="notification is-danger is-light">{loadError}</div>}

      {!loading && !loadError && rules.length === 0 && (
        <div className="notification is-light">{PORT_REDIRECT_MESSAGES.empty}</div>
      )}

      {rules.length > 0 && visibleRows.length === 0 && (
        <div className="notification is-light">{PORT_REDIRECT_MESSAGES.noMatch}</div>
      )}

      {visibleRows.length > 0 && (
        <div className="table-container">
          <table className="table is-fullwidth is-hoverable is-striped">
            <thead>
              <tr>
                <th>IP Origem</th>
                <th>Porta Origem</th>
                <th>Protocolo</th>
                <th>Porta Destino</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr key={row.id}>
                  <td>{row.sourceIp}</td>
                  <td>
                    {row.sourcePorts}
                    {!row.applied && <span className="tag is-warning is-light port-redirects-tag">{PORT_REDIRECT_MESSAGES.notApplied}</span>}
                  </td>
                  <td>{row.protocol}</td>
                  <td>{row.destinationPort}</td>
                  <td>
                    <button type="button" className="button is-small is-danger is-light" onClick={() => openDelete(row)}>
                      Excluir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        open={!!ruleToDelete}
        danger
        title="Excluir regra de redirect"
        body={(
          <>
            <p>
              {ruleToDelete
                ? `Excluir o redirect ${ruleToDelete.sourcePorts} (${ruleToDelete.protocol}) → ${ruleToDelete.destinationPort}, origem ${ruleToDelete.sourceIp}? A regra também será removida do servidor.`
                : ''}
            </p>
            {deleteError && <p className="has-text-danger mt-2"><strong>{deleteError}</strong></p>}
          </>
        )}
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        loadingLabel="Excluindo..."
        loading={deleting}
        onCancel={closeDelete}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

export default PortRedirects;
